import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/utils";
import config from "@/config";

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_CHUNK_SIZE = 20;
const DEFAULT_BATCH_OFFSET_MINUTES = 20;

async function readErrorMessage(res: Response): Promise<string> {
  const raw = await res.text();
  if (!raw.trim()) {
    return res.statusText || `Request failed (${res.status})`;
  }
  try {
    const data = JSON.parse(raw) as { detail?: unknown };
    const d = data.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
      return d
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && "msg" in item) {
            return String((item as { msg: unknown }).msg);
          }
          return JSON.stringify(item);
        })
        .join("; ");
    }
  } catch {
    return raw;
  }
  return raw;
}

type ResultCategory = "create" | "retry" | "skip" | "issues";

type IssueRow =
  | { kind: "invalid"; row: DashboardInvalidCase }
  | { kind: "lookup"; row: DashboardValidateError };

interface ValidationRow {
  row_number: number;
  is_valid: boolean;
  missing_fields: string[];
  invalid_phone?: string;
  data?: Record<string, unknown>;
}

interface DashboardCaseRef {
  row_number: number;
  case_id: string;
}

interface DashboardSkipCase extends DashboardCaseRef {
  batch_id?: string | null;
  /** e.g. already_successful | active_batch | not_failed_status | max_retries */
  skip_reason?: string;
}

interface DashboardInvalidCase {
  row_number: number;
  missing_fields: string[];
  invalid_phone?: string;
  data?: Record<string, unknown>;
}

interface DashboardValidateError {
  row_number: number;
  case_id?: string;
  error: string;
}

interface DashboardValidateCsvResponse {
  success: boolean;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  auto_filled_count?: number;
  validation_results: ValidationRow[];
  create_cases: DashboardCaseRef[];
  retry_cases: DashboardCaseRef[];
  skip_cases: DashboardSkipCase[];
  invalid_cases: DashboardInvalidCase[];
  errors: DashboardValidateError[];
}

interface ProcessCasesResponse {
  success: boolean;
  batch_id?: string;
  assistant_id?: string;
  cases_created?: number;
  cases_skipped?: number;
  cases_processed?: number;
  cases_invalid?: number;
  cases_errors?: number;
  cases_total?: number;
  errors?: { case_id?: string; error: string }[];
  /** True when outbound (phone resolution + VAPI) runs in background after persist. */
  deferred_telephony?: boolean;
}

type RowImportStatus = "idle" | "pending" | "success" | "error";

function pickCell(data: Record<string, unknown> | undefined, keys: string[]): string {
  if (!data) return "—";
  for (const k of keys) {
    const v = data[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "—";
}

function rowDataFor(
  validation: DashboardValidateCsvResponse,
  rowNumber: number,
): Record<string, unknown> | undefined {
  return validation.validation_results.find((r) => r.row_number === rowNumber)?.data;
}

function buildIssueRows(v: DashboardValidateCsvResponse): IssueRow[] {
  const list: IssueRow[] = [
    ...(v.invalid_cases ?? []).map((row) => ({ kind: "invalid" as const, row })),
    ...(v.errors ?? []).map((row) => ({ kind: "lookup" as const, row })),
  ];
  list.sort((a, b) => {
    const ra = a.kind === "invalid" ? a.row.row_number : a.row.row_number;
    const rb = b.kind === "invalid" ? b.row.row_number : b.row.row_number;
    return ra - rb;
  });
  return list;
}

function defaultResultCategory(v: DashboardValidateCsvResponse): ResultCategory {
  const nIssues = (v.invalid_cases?.length ?? 0) + (v.errors?.length ?? 0);
  const counts: Record<ResultCategory, number> = {
    create: v.create_cases?.length ?? 0,
    retry: v.retry_cases?.length ?? 0,
    skip: v.skip_cases?.length ?? 0,
    issues: nIssues,
  };
  const order: ResultCategory[] = ["create", "retry", "skip", "issues"];
  const hit = order.find((c) => counts[c] > 0);
  return hit ?? "create";
}

/** Counts skipped CSV rows by backend `skip_reason` (validate-csv). */
function summarizeSkipCases(skipCases: DashboardSkipCase[]) {
  let alreadySuccessful = 0;
  let activeBatch = 0;
  let retryLimitReached = 0;
  let other = 0;
  for (const row of skipCases) {
    switch (row.skip_reason) {
      case "already_successful":
        alreadySuccessful += 1;
        break;
      case "active_batch":
        activeBatch += 1;
        break;
      case "max_retries":
        retryLimitReached += 1;
        break;
      default:
        other += 1;
        break;
    }
  }
  return {
    alreadySuccessful,
    activeBatch,
    retryLimitReached,
    other,
    total: skipCases.length,
  };
}

export interface UploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful import so lists can refresh */
  onUploaded?: () => void;
}

export function UploadModal({ open, onOpenChange, onUploaded }: UploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validation, setValidation] = useState<DashboardValidateCsvResponse | null>(null);
  const [activeCategory, setActiveCategory] = useState<ResultCategory>("create");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState(String(DEFAULT_BATCH_SIZE));
  const [chunkSize, setChunkSize] = useState(String(DEFAULT_CHUNK_SIZE));
  const [batchOffsetMinutes, setBatchOffsetMinutes] = useState(String(DEFAULT_BATCH_OFFSET_MINUTES));
  const [rowImportStatus, setRowImportStatus] = useState<Record<string, RowImportStatus>>({});
  const [uploadChunkProgress, setUploadChunkProgress] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setValidation(null);
    setStatus("idle");
    setMessage(null);
    setDragActive(false);
    setActiveCategory("create");
    setBatchSize(String(DEFAULT_BATCH_SIZE));
    setChunkSize(String(DEFAULT_CHUNK_SIZE));
    setBatchOffsetMinutes(String(DEFAULT_BATCH_OFFSET_MINUTES));
    setRowImportStatus({});
    setUploadChunkProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (validation) setActiveCategory(defaultResultCategory(validation));
  }, [validation]);

  const pickCsv = useCallback((f: File | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setStatus("error");
      setMessage("Please choose a .csv file.");
      return;
    }
    setFile(f);
    setValidation(null);
    setStatus("idle");
    setMessage(null);
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pickCsv(e.target.files?.[0]);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    pickCsv(e.dataTransfer.files?.[0]);
  };

  const postFile = async (path: string): Promise<Response> => {
    if (!file) throw new Error("No file selected");
    const body = new FormData();
    body.append("file", file);
    return fetch(`${config.API_BASE_URL}${path}`, {
      method: "POST",
      body,
    });
  };

  const handleValidate = async () => {
    if (!file) return;
    setValidating(true);
    setStatus("idle");
    setMessage(null);
    try {
      const res = await postFile("/api/dashboard/validate-csv");
      if (!res.ok) {
        setStatus("error");
        setMessage(await readErrorMessage(res));
        setValidation(null);
        return;
      }
      const data = (await res.json()) as DashboardValidateCsvResponse;
      setValidation(data);
      setStatus("idle");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Validation request failed");
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const handleProcessCases = async () => {
    if (!validation) return;

    const bs = Math.min(100, Math.max(1, parseInt(batchSize, 10) || DEFAULT_BATCH_SIZE));
    const cs = Math.min(100, Math.max(1, parseInt(chunkSize, 10) || DEFAULT_CHUNK_SIZE));
    const off = Math.max(0, parseInt(batchOffsetMinutes, 10) || DEFAULT_BATCH_OFFSET_MINUTES);

    const refs = [...(validation.create_cases ?? []), ...(validation.retry_cases ?? [])];
    const cases = refs.map((ref) => {
      const data = rowDataFor(validation, ref.row_number);
      return { case_id: ref.case_id, data: (data ?? {}) as Record<string, unknown> };
    });

    const pendingMap: Record<string, RowImportStatus> = {};
    for (const c of cases) {
      pendingMap[c.case_id] = "pending";
    }
    setRowImportStatus(pendingMap);
    setUploading(true);
    setUploadChunkProgress(null);
    setStatus("idle");
    setMessage(null);

    const chunkSizeUsed = cs;
    const nChunks = Math.ceil(cases.length / chunkSizeUsed) || 1;
    let totalCreated = 0;
    let totalSkipped = 0;
    let totalProcessed = 0;
    let totalCaseErrors = 0;
    const mergedErrors: { case_id?: string; error: string }[] = [];
    let lastBatchId: string | undefined;
    let anyDeferred = false;
    let fatalHttpError: string | null = null;

    try {
      for (let i = 0; i < cases.length; i += chunkSizeUsed) {
        const chunk = cases.slice(i, i + chunkSizeUsed);
        const chunkIndex = Math.floor(i / chunkSizeUsed) + 1;
        setUploadChunkProgress(`Sending ${chunkIndex}/${nChunks} (${chunk.length} case${chunk.length === 1 ? "" : "s"})…`);

        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/process-cases`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cases: chunk,
            batch_size: bs,
            batch_offset_minutes: off,
            errors: [],
          }),
        });

        if (!res.ok) {
          const errText = await readErrorMessage(res);
          fatalHttpError = errText;
          setRowImportStatus((prev) => {
            const next = { ...prev };
            for (const c of chunk) {
              next[c.case_id] = "error";
            }
            for (const k of Object.keys(next)) {
              if (next[k] === "pending") next[k] = "error";
            }
            return next;
          });
          break;
        }

        const data = (await res.json()) as ProcessCasesResponse;
        if (data.deferred_telephony) anyDeferred = true;
        if (data.batch_id) lastBatchId = data.batch_id;
        totalCreated += data.cases_created ?? 0;
        totalSkipped += data.cases_skipped ?? 0;
        totalProcessed += data.cases_processed ?? 0;
        totalCaseErrors += data.cases_errors ?? data.errors?.length ?? 0;
        if (data.errors?.length) mergedErrors.push(...data.errors);

        const erroredIds = new Set(
          (data.errors ?? []).map((e) => e.case_id).filter((id): id is string => Boolean(id && String(id).trim())),
        );

        setRowImportStatus((prev) => {
          const next = { ...prev };
          for (const c of chunk) {
            next[c.case_id] = erroredIds.has(c.case_id) ? "error" : "success";
          }
          return next;
        });
      }

      setUploadChunkProgress(null);

      if (fatalHttpError) {
        setStatus("error");
        setMessage(fatalHttpError);
        return;
      }

      setStatus("success");
      const outboundMsg =
        anyDeferred ?
          `Queued ${totalProcessed} case(s) for outbound (background).`
        : `Processed ${totalProcessed} call(s).`;
      const parts = [
        outboundMsg,
        `Created ${totalCreated}, skipped ${totalSkipped} (already successful or active batch).`,
      ];
      if (lastBatchId) parts.push(`Last batch ${lastBatchId}.`);
      if (nChunks > 1) parts.push(`${nChunks} import requests (${chunkSizeUsed} cases max each).`);
      if (totalCaseErrors > 0 || mergedErrors.length > 0) {
        parts.push(`Row-level errors: ${totalCaseErrors || mergedErrors.length}.`);
      }
      setMessage(parts.join(" "));
      setFile(null);
      setValidation(null);
      setRowImportStatus({});
      setBatchSize(String(DEFAULT_BATCH_SIZE));
      setChunkSize(String(DEFAULT_CHUNK_SIZE));
      setBatchOffsetMinutes(String(DEFAULT_BATCH_OFFSET_MINUTES));
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.();
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Process cases failed");
      setRowImportStatus((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (next[k] === "pending") next[k] = "error";
        }
        return next;
      });
    } finally {
      setUploading(false);
      setUploadChunkProgress(null);
    }
  };

  const backToFilePicker = useCallback(() => {
    setValidation(null);
    setFile(null);
    setMessage(null);
    setStatus("idle");
    setBatchSize(String(DEFAULT_BATCH_SIZE));
    setChunkSize(String(DEFAULT_CHUNK_SIZE));
    setBatchOffsetMinutes(String(DEFAULT_BATCH_OFFSET_MINUTES));
    setRowImportStatus({});
    setUploadChunkProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const busy = validating || uploading;

  const nCreate = validation?.create_cases?.length ?? 0;
  const nRetry = validation?.retry_cases?.length ?? 0;
  const nSkip = validation?.skip_cases?.length ?? 0;
  const nIssues =
    (validation?.invalid_cases?.length ?? 0) + (validation?.errors?.length ?? 0);
  const nProcessable = (validation?.create_cases?.length ?? 0) + (validation?.retry_cases?.length ?? 0);

  const skipStats = useMemo(
    () => summarizeSkipCases(validation?.skip_cases ?? []),
    [validation?.skip_cases],
  );

  const categoryButton = (
    id: ResultCategory,
    label: string,
    count: number,
  ) => (
    <Button
      type="button"
      variant={activeCategory === id ? "default" : "outline"}
      size="sm"
      className="h-auto shrink-0 flex-col gap-0.5 py-2 px-3 sm:flex-row sm:gap-2"
      disabled={count === 0}
      onClick={() => setActiveCategory(id)}
    >
      <span>{label}</span>
      <span
        className={cn(
          "tabular-nums text-xs font-semibold",
          activeCategory === id ? "opacity-90" : "text-muted-foreground",
        )}
      >
        {count}
      </span>
    </Button>
  );

  const renderResultsTable = () => {
    if (!validation) return null;

    const th = "border-b bg-muted/60 px-2 py-2 text-left text-xs font-medium text-muted-foreground";
    const td = "border-b border-border/50 px-2 py-2 text-xs align-top";
    const showImportStatus = activeCategory === "create" || activeCategory === "retry";

    if (activeCategory === "issues") {
      const issueRows = buildIssueRows(validation);
      return (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr>
                <th className={th}>Row</th>
                <th className={th}>Type</th>
                <th className={th}>Case ID</th>
                <th className={th}>Member</th>
                <th className={th}>DOB</th>
                <th className={th}>BIN</th>
                <th className={th}>PCN</th>
                <th className={th}>Product</th>
                <th className={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {issueRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className={cn(td, "text-muted-foreground")}>
                    No issues.
                  </td>
                </tr>
              ) : (
                issueRows.map((item, idx) => {
                  if (item.kind === "invalid") {
                    const row = item.row;
                    const data = row.data ?? rowDataFor(validation, row.row_number);
                    const first = pickCell(data, ["Member First Name", "member_first_name"]);
                    const last = pickCell(data, ["Member Last Name", "member_last_name"]);
                    const member =
                      first !== "—" || last !== "—" ? `${first} ${last}`.trim() : "—";
                    const issues = [
                      ...row.missing_fields,
                      ...(row.invalid_phone ? [row.invalid_phone] : []),
                    ].join("; ");
                    return (
                      <tr key={`inv-${row.row_number}`}>
                        <td className={td}>{row.row_number}</td>
                        <td className={td}>Invalid row</td>
                        <td className={cn(td, "max-w-[140px] break-all font-mono text-[11px]")}>
                          {pickCell(data, ["Case ID", "case_id"])}
                        </td>
                        <td className={td}>{member}</td>
                        <td className={td}>
                          {pickCell(data, ["Member Date of Birth", "member_date_of_birth"])}
                        </td>
                        <td className={td}>{pickCell(data, ["Member BIN", "member_bin"])}</td>
                        <td className={td}>{pickCell(data, ["Member PCN", "member_pcn"])}</td>
                        <td className={td}>{pickCell(data, ["Product Name #1", "product_name_1"])}</td>
                        <td className={cn(td, "max-w-[240px] break-words text-destructive")}>
                          {issues || "—"}
                        </td>
                      </tr>
                    );
                  }
                  const err = item.row;
                  const data = rowDataFor(validation, err.row_number);
                  const first = pickCell(data, ["Member First Name", "member_first_name"]);
                  const last = pickCell(data, ["Member Last Name", "member_last_name"]);
                  const member =
                    first !== "—" || last !== "—" ? `${first} ${last}`.trim() : "—";
                  return (
                    <tr key={`lk-${err.row_number}-${idx}`}>
                      <td className={td}>{err.row_number}</td>
                      <td className={td}>Lookup failed</td>
                      <td className={cn(td, "max-w-[140px] break-all font-mono text-[11px]")}>
                        {err.case_id ?? "—"}
                      </td>
                      <td className={td}>{member}</td>
                      <td className={td}>
                        {pickCell(data, ["Member Date of Birth", "member_date_of_birth"])}
                      </td>
                      <td className={td}>{pickCell(data, ["Member BIN", "member_bin"])}</td>
                      <td className={td}>{pickCell(data, ["Member PCN", "member_pcn"])}</td>
                      <td className={td}>{pickCell(data, ["Product Name #1", "product_name_1"])}</td>
                      <td className={cn(td, "max-w-[240px] break-words text-destructive")}>
                        {err.error}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      );
    }

    const caseRows =
      activeCategory === "create" ? validation.create_cases
      : activeCategory === "retry" ? validation.retry_cases
      : validation.skip_cases;

    return (
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr>
              {showImportStatus ? <th className={th}>Import</th> : null}
              <th className={th}>Row</th>
              <th className={th}>Case ID</th>
              <th className={th}>Member</th>
              <th className={th}>DOB</th>
              <th className={th}>BIN</th>
              <th className={th}>PCN</th>
              <th className={th}>Product</th>
            </tr>
          </thead>
          <tbody>
            {caseRows.length === 0 ? (
              <tr>
                <td
                  colSpan={showImportStatus ? 8 : 7}
                  className={cn(td, "text-muted-foreground")}
                >
                  No rows in this category.
                </td>
              </tr>
            ) : (
              caseRows.map((row) => {
                const data = rowDataFor(validation, row.row_number);
                const first = pickCell(data, ["Member First Name", "member_first_name"]);
                const last = pickCell(data, ["Member Last Name", "member_last_name"]);
                const member =
                  first !== "—" || last !== "—" ? `${first} ${last}`.trim() : "—";
                const st = rowImportStatus[row.case_id] ?? "idle";
                return (
                  <tr key={`${activeCategory}-${row.row_number}-${row.case_id}`}>
                    {showImportStatus ? (
                      <td className={cn(td, "w-10 whitespace-nowrap")}>
                        {st === "pending" ? (
                          <Loader2
                            className="h-4 w-4 animate-spin text-muted-foreground"
                            aria-label="Waiting for import response"
                          />
                        ) : st === "success" ? (
                          <CheckCircle2
                            className="h-4 w-4 text-green-600 dark:text-green-400"
                            aria-label="Import queued"
                          />
                        ) : st === "error" ? (
                          <AlertCircle className="h-4 w-4 text-destructive" aria-label="Import failed" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    ) : null}
                    <td className={td}>{row.row_number}</td>
                    <td className={cn(td, "max-w-[180px] break-all font-mono text-[11px]")}>
                      {row.case_id}
                    </td>
                    <td className={td}>{member}</td>
                    <td className={td}>
                      {pickCell(data, ["Member Date of Birth", "member_date_of_birth"])}
                    </td>
                    <td className={td}>{pickCell(data, ["Member BIN", "member_bin"])}</td>
                    <td className={td}>{pickCell(data, ["Member PCN", "member_pcn"])}</td>
                    <td className={td}>{pickCell(data, ["Product Name #1", "product_name_1"])}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("max-h-[90vh] overflow-y-auto", validation ? "max-w-4xl sm:max-w-4xl" : "max-w-lg sm:max-w-lg")}
      >
        <DialogHeader>
          <DialogTitle>Upload cases CSV</DialogTitle>
          {!validation ? (
            <DialogDescription>
              Drop a CSV, validate to classify rows, then import Create and Retry rows via process-cases.
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <div>
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={onInputChange} />
          {!validation ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (e.currentTarget === e.target) setDragActive(false);
                }}
                onDrop={onDrop}
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center text-sm transition-colors",
                  dragActive ? "border-primary bg-muted/40" : "border-muted-foreground/25",
                  busy && "pointer-events-none opacity-60",
                )}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="font-medium text-foreground">Drop a CSV here or click to browse</span>
                <span className="text-xs text-muted-foreground">.csv only</span>
              </button>

              {file ? (
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                  <Button type="button" variant="ghost" size="sm" className="shrink-0" disabled={busy} onClick={reset}>
                    Clear
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{file?.name ?? "CSV"}</span>
                  <span className="text-muted-foreground">
                    · {validation.total_rows} row{validation.total_rows === 1 ? "" : "s"} · schema valid{" "}
                    {validation.valid_rows} / invalid {validation.invalid_rows}
                  </span>
                </div>
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={backToFilePicker}>
                  Choose different file
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-2">
                  <label htmlFor="upload-chunk-size" className="block text-xs font-medium text-muted-foreground">
                    Import chunk size
                  </label>
                  <Input
                    id="upload-chunk-size"
                    type="number"
                    min={1}
                    max={100}
                    inputMode="numeric"
                    value={chunkSize}
                    onChange={(e) => setChunkSize(e.target.value)}
                    disabled={busy}
                    className="h-9"
                  />
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    Cases per request to the server (smaller = more requests, less payload per call).
                  </p>
                </div>
                <div className="space-y-2">
                  <label htmlFor="upload-batch-size" className="block text-xs font-medium text-muted-foreground">
                    Outbound batch size
                  </label>
                  <Input
                    id="upload-batch-size"
                    type="number"
                    min={1}
                    max={100}
                    inputMode="numeric"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    disabled={busy}
                    className="h-9"
                  />
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    Backend VAPI schedule window: max cases sharing one <code className="rounded bg-muted px-0.5">schedulePlan</code> per import batch.
                  </p>
                </div>
                <div className="space-y-2">
                  <label htmlFor="upload-batch-offset" className="block text-xs font-medium text-muted-foreground">
                    Batch offset (minutes)
                  </label>
                  <Input
                    id="upload-batch-offset"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={batchOffsetMinutes}
                    onChange={(e) => setBatchOffsetMinutes(e.target.value)}
                    disabled={busy}
                    className="h-9"
                  />
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    Stagger between outbound schedule chunks (same as backend <code className="rounded bg-muted px-0.5">process-cases</code>).
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {categoryButton("create", "Create", nCreate)}
                {categoryButton("retry", "Retry", nRetry)}
                {categoryButton("skip", "Skip (in retry state)", nSkip)}
                {categoryButton("issues", "Issues", nIssues)}
              </div>

              {nSkip > 0 ? (
                <div className="rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-xs">
                  <p className="font-medium text-foreground">Skipped rows — breakdown</p>
                  <p className="mt-1 leading-relaxed text-muted-foreground">
                    <span className="tabular-nums font-semibold text-foreground">
                      {skipStats.alreadySuccessful}
                    </span>{" "}
                    already successful
                    <span className="mx-1.5 text-border">·</span>
                    <span className="tabular-nums font-semibold text-foreground">{skipStats.activeBatch}</span>{" "}
                    active (in batch retry)
                    <span className="mx-1.5 text-border">·</span>
                    <span className="tabular-nums font-semibold text-foreground">
                      {skipStats.retryLimitReached}
                    </span>{" "}
                    retry limit reached
                    {skipStats.other > 0 ? (
                      <>
                        <span className="mx-1.5 text-border">·</span>
                        <span className="tabular-nums font-semibold text-foreground">{skipStats.other}</span>{" "}
                        other
                      </>
                    ) : null}
                  </p>
                </div>
              ) : null}

              <ScrollArea className="h-[min(52vh,420px)] w-full rounded-md border">
                <div className="p-1">{renderResultsTable()}</div>
              </ScrollArea>
            </div>
          )}

          {message ? (
            <div
              className={cn(
                "flex gap-2 rounded-md border p-3 text-sm",
                status === "success"
                  ? "border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950/40 dark:text-green-100"
                  : status === "error"
                    ? "border-destructive/40 bg-destructive/10 text-destructive"
                    : "border-border",
              )}
              role="status"
            >
              {status === "success" ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
              ) : status === "error" ? (
                <AlertCircle className="h-4 w-4 shrink-0" />
              ) : null}
              <span>{message}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || (validation ? nProcessable === 0 : !file)}
            onClick={validation ? handleProcessCases : handleValidate}
          >
            {validating ?
              "Validating…"
            : uploading ?
              uploadChunkProgress || "Processing…"
            : validation ?
              "Import"
            : "Validate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
